import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n";

export const BrandMark = () => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <div className="flex items-end gap-[3px]">
      {[12, 18, 24, 30].map((h, i) => (
        <span
          key={i}
          style={{ height: `${h}px` }}
          className="w-[3px] rounded-sm bg-gradient-to-t from-[#5E4A2E] to-[#C09A6B]"
        />
      ))}
      <span className="w-[3px] h-9 rounded-sm bg-gradient-to-t from-[#6E4E2E] to-[#A87C4F]" />
    </div>
    <div className="leading-none">
      <p className="font-display text-2xl tracking-[0.22em] text-[#3B322A]">EBS</p>
      <p className="mt-1 text-[9px] uppercase tracking-[0.32em] text-[#948678]">Hotels Revenue</p>
    </div>
  </div>
);

const LangSwitcher = ({ variant = "desktop" }) => {
  const { lang, setLang } = useLang();
  const suffix = variant === "mobile" ? "-mobile" : "";
  return (
    <div className="flex items-center gap-1 rounded-sm border border-[#A87C4F]/40 bg-white/60 p-1" data-testid={`language-switcher${suffix}`}>
      {["fr", "en", "it", "bg"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}-button${suffix}`}
          className={`rounded-[2px] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
            lang === l ? "bg-[#A87C4F] text-white" : "text-[#948678] hover:text-[#856237]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "glass-nav" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#accueil" aria-label="EBS Hotels Revenue">
          <BrandMark />
        </a>

        <ul className="hidden items-center gap-7 lg:flex">
          {t.nav.links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={l.testid}
                className="text-[13px] font-medium uppercase tracking-[0.14em] text-[#5F5647] transition-colors duration-300 hover:text-[#856237]"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitcher />
          <a
            href="#rendez-vous"
            data-testid="nav-cta-audit-button"
            className="btn-gold inline-block whitespace-nowrap rounded-sm px-6 py-2.5 text-[13px] font-semibold uppercase tracking-[0.14em]"
          >
            {t.nav.cta}
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangSwitcher variant="mobile" />
          <button
            className="text-[#6E4E2E]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="glass-nav border-t border-[#A87C4F]/10 px-5 pb-6 pt-2 lg:hidden">
          <ul className="flex flex-col gap-1">
            {t.nav.links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className="block rounded-sm px-3 py-3 text-sm uppercase tracking-[0.14em] text-[#5F5647] transition-colors hover:bg-[#A87C4F]/10 hover:text-[#856237]"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li className="pt-3">
              <a
                href="#rendez-vous"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-audit-button-mobile"
                className="btn-gold block rounded-sm px-6 py-3 text-center text-[13px] font-semibold uppercase tracking-[0.14em]"
              >
                {t.nav.cta}
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
